/* Owner ERP Dashboard — Slice C (real APIs). Design: /app/design_guidelines.json */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import {
  LayoutDashboard, FolderTree, Users, Wallet, BarChart3, ScrollText, CreditCard,
  CalendarRange, ChevronLeft, Crown, QrCode, Smartphone, CircleDollarSign,
  LogOut, UserCircle, ChevronsUpDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuthContext } from '../../context/AuthContext';
import { API } from '../../utils/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { NotificationBell } from '../../components/NotificationBell';
import {
  TXT, TEAL, fmtKr, fmtDate, Fonts, Panel, SectionTitle, KpiCard, Meter, WORK_SANS,
} from './ownerUi';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { DivisionsSection, MembersSection, SwishSection } from './ownerSections';
import { ReportsSection, AuditSection, BillingSection, OwnerProfileSection } from './ownerSections2';

function rangeDates(key) {
  const to = new Date();
  const from = new Date();
  if (key === '7d') from.setDate(to.getDate() - 7);
  else if (key === '30d') from.setDate(to.getDate() - 30);
  else if (key === '90d') from.setDate(to.getDate() - 90);
  else { from.setMonth(0, 1); from.setHours(0, 0, 0, 0); }
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function OwnerDashboard() {
  const auth = useAuthContext();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const lang = (i18n.language || 'en').startsWith('sv') ? 'sv' : 'en';
  const t = TXT[lang];
  const [nav, setNav] = useState('overview');
  const [rangeKey, setRangeKey] = useState('30d');
  const [reportDivision, setReportDivision] = useState('all');
  const range = useMemo(() => rangeDates(rangeKey), [rangeKey]);

  // Two useAuth() instances exist (App + Gate). After in-app login the App-level
  // (visible here) is stale, so fall back to localStorage for the same session.
  const token = auth?.token || localStorage.getItem('token');
  const user = useMemo(() => {
    if (auth?.user) return auth.user;
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, [auth?.user]);
  const headers = useMemo(
    () => ({ Authorization: token ? `Bearer ${token}` : '' }),
    [token],
  );

  const [bizLogo, setBizLogo] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/business/profile`, { headers })
      .then((r) => setBizLogo(r.data && r.data.logo_path ? `${API}/business/logo-file/${r.data.logo_path}` : null)).catch(() => {});
  }, [headers, token]);

  const orgId = user?.id || user?._id;
  const orgName = user?.business_info?.company_name || user?.full_name || t.organisation;

  useEffect(() => {
    // Owner ERP is desktop/tablet-only. On mobile, business accounts see /business.
    if (isMobile && user?.user_type === 'BUSINESS') navigate('/', { replace: true });
  }, [isMobile, user, navigate]);

  if (!token || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Button onClick={() => navigate('/')} className="bg-teal-700 hover:bg-teal-800 text-white">PayMig</Button>
      </div>
    );
  }
  if (user.user_type !== 'BUSINESS') {
    return (
      <div className="min-h-screen flex flex-col gap-3 items-center justify-center bg-slate-50 text-slate-600 text-sm">
        <p>{t.not_business}</p>
        <Button onClick={() => navigate('/')} variant="outline">{t.go_home}</Button>
      </div>
    );
  }

  const NAV = [
    { id: 'overview', icon: LayoutDashboard, label: t.nav_overview },
    { id: 'swish', icon: Wallet, label: t.nav_swish },
    { id: 'members', icon: Users, label: t.nav_members },
    { id: 'divisions', icon: FolderTree, label: t.nav_divisions },
    { id: 'reports', icon: BarChart3, label: t.nav_reports },
    { id: 'audit', icon: ScrollText, label: t.nav_audit },
    { id: 'billing', icon: CreditCard, label: t.nav_billing },
  ];
  const rangeLabel = { '7d': t.range_7d, '30d': t.range_30d, '90d': t.range_90d, ytd: t.range_ytd };
  const ctx = { orgId, headers, t, lang, range, setNav, reportDivision, setReportDivision };

  const doLogout = async () => {
    try { await auth?.logout?.(); } catch (_) { /* noop */ }
    try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch (_) { /* noop */ }
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Fonts />
      <Toaster position="top-right" richColors />

      <aside className="fixed inset-y-0 left-0 w-[232px] bg-white border-r border-slate-200 flex-col z-30 hidden lg:flex">
        <div className="h-14 px-4 flex items-center gap-2.5 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white font-bold text-sm" style={WORK_SANS}>P</div>
          <div className="leading-tight">
            <div className="font-semibold text-[14px]" style={WORK_SANS}>PayMig</div>
            <div className="text-[10.5px] text-slate-400 font-medium tracking-wide uppercase">{t.owner_erp}</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV.map(({ id, icon: Icon, label }) => (
            <button key={id} data-testid={`owner-nav-${id}`} onClick={() => setNav(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-colors relative ${
                nav === id ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
              {nav === id && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-teal-600" />}
              <Icon size={18} className={nav === id ? 'text-teal-600' : 'text-slate-400'} strokeWidth={1.5} />
              <span className="flex-1 text-left">{label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <button onClick={() => navigate('/')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-50"
            data-testid="owner-back-to-app">
            <ChevronLeft size={15} />{t.back_to_app}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="owner-account-menu"
                className="w-full flex items-center gap-2.5 px-2 py-1.5 mt-1 rounded-lg hover:bg-slate-50 transition-colors">
                {bizLogo ? (
                  <img src={bizLogo} alt="logo" className="w-8 h-8 rounded-full object-cover border border-slate-200" data-testid="owner-sidebar-logo" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center text-[12px] font-bold">
                    {(user.full_name || 'O').split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="leading-tight min-w-0 flex-1 text-left">
                  <div className="text-[13px] font-semibold truncate">{user.full_name}</div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-1"><Crown size={11} className="text-amber-500" />{t.owner}</div>
                </div>
                <ChevronsUpDown size={14} className="text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-[200px]">
              <DropdownMenuItem data-testid="owner-menu-profile" onClick={() => setNav('profile')}>
                <UserCircle size={15} className="mr-2 text-slate-500" />{t.profile}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="owner-menu-logout" onClick={doLogout}
                className="text-rose-600 focus:text-rose-700">
                <LogOut size={15} className="mr-2" />{t.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="lg:pl-[232px]">
        <header className="sticky top-0 z-20 h-14 backdrop-blur-xl bg-white/70 border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-md bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {orgName.slice(0, 2).toUpperCase()}
            </div>
            <span className="text-[13.5px] font-semibold truncate" style={WORK_SANS} data-testid="owner-org-name">{orgName}</span>
            <span className="text-[12px] text-slate-400 hidden sm:inline">▸ {t.all_divisions}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Select value={rangeKey} onValueChange={setRangeKey}>
              <SelectTrigger data-testid="owner-date-range" className="h-9 w-[170px] text-[13px] font-medium bg-white border-slate-200 gap-1.5">
                <CalendarRange size={14} className="text-teal-600 shrink-0" />
                <SelectValue>{rangeLabel[rangeKey]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">{t.range_7d}</SelectItem>
                <SelectItem value="30d">{t.range_30d}</SelectItem>
                <SelectItem value="90d">{t.range_90d}</SelectItem>
                <SelectItem value="ytd">{t.range_ytd}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
              {['en', 'sv'].map((l) => (
                <button key={l} data-testid={`owner-lang-${l}`} onClick={() => i18n.changeLanguage(l)}
                  className={`px-2.5 py-1.5 text-[12px] font-semibold uppercase transition-colors ${lang === l ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                  {l}
                </button>
              ))}
            </div>
            <NotificationBell />
          </div>
        </header>

        <main className="p-4 lg:p-6 max-w-[1440px]">
          {nav === 'overview' && <OverviewSection {...ctx} />}
          {nav === 'divisions' && <DivisionsSection {...ctx} />}
          {nav === 'members' && <MembersSection {...ctx} />}
          {nav === 'swish' && <SwishSection {...ctx} />}
          {nav === 'reports' && <ReportsSection {...ctx} />}
          {nav === 'audit' && <AuditSection {...ctx} />}
          {nav === 'billing' && <BillingSection {...ctx} />}
          {nav === 'profile' && <OwnerProfileSection {...ctx} user={user} onLogout={doLogout} onLogoChange={setBizLogo} />}
        </main>
      </div>
    </div>
  );
}

/* ============================== Overview ============================== */

function OverviewSection({ orgId, headers, t, lang, range, setNav }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr(false);
      const res = await axios.get(`${API}/org/${orgId}/stats`, {
        headers, params: { from: range.from, to: range.to }, timeout: 20000 });
      setStats(res.data);
    } catch (e) { console.error(e); setErr(true); }
  }, [orgId, headers, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-rose-600">{t.error_load}</p>;
  if (!stats) return <p className="text-sm text-slate-400">{t.loading}</p>;

  const k = stats.kpis;
  const mixTotal = stats.payment_mix.reduce((a, m) => a + m.value, 0);
  const mixData = stats.payment_mix.map((m) => ({ ...m, label: m.key === 'app2app' ? t.mix_app2app : t.mix_guest_qr }));
  const usage = stats.usage || {};

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard testId="okpi-collected" title={t.kpi_collected} value={fmtKr(k.total_collected.value)} deltaPct={k.total_collected.delta_pct}
                 spark={stats.timeseries.map((p) => p.value)} />
        <KpiCard testId="okpi-tx" title={t.kpi_tx} value={new Intl.NumberFormat('sv-SE').format(k.transactions.value)} deltaPct={k.transactions.delta_pct} />
        <KpiCard testId="okpi-avg" title={t.kpi_avg} value={fmtKr(k.avg_payment.value)} deltaPct={k.avg_payment.delta_pct} />
        <KpiCard testId="okpi-payers" title={t.kpi_payers} value={new Intl.NumberFormat('sv-SE').format(k.unique_payers.value)} deltaPct={k.unique_payers.delta_pct} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
        <Panel testId="owner-hero-chart" className="p-5">
          <div className="flex items-center justify-between mb-4">
            <SectionTitle sub={`${range.from} → ${range.to} · SEK`}>{t.collections_over_time}</SectionTitle>
            <Badge className="bg-teal-50 text-teal-700 border border-teal-200 shadow-none hover:bg-teal-50">{fmtKr(k.total_collected.value)}</Badge>
          </div>
          {stats.timeseries.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-16 text-center">{t.no_data}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={stats.timeseries} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="ownerHeroFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                       tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={38} />
                <RTooltip formatter={(v) => [fmtKr(v), t.kpi_collected]}
                          contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Area type="monotone" dataKey="value" stroke="#0D9488" strokeWidth={2.5} fill="url(#ownerHeroFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel testId="owner-activity-rail" className="p-5 flex flex-col max-h-[420px]">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle>{t.recent_activity}</SectionTitle>
            <button className="text-[12px] text-teal-700 font-medium hover:underline" onClick={() => setNav('audit')}>{t.view_all}</button>
          </div>
          <div className="space-y-1 overflow-y-auto -mx-1">
            {stats.recent_transactions.length === 0 && <p className="text-[13px] text-slate-400 py-6 text-center">{t.no_data}</p>}
            {stats.recent_transactions.map((tx, i) => (
              <div key={i} className="flex items-start gap-2.5 px-1 py-2 rounded-lg hover:bg-slate-50">
                <span className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${tx.is_guest ? 'bg-sky-50 text-sky-700' : 'bg-teal-50 text-teal-700'}`}>
                  {tx.is_guest ? <QrCode size={14} /> : <CircleDollarSign size={14} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] leading-snug text-slate-700 truncate">
                    <span className="font-semibold">{fmtKr(tx.amount)}</span>
                    {' — '}{tx.is_guest ? `${t.guest}: ` : ''}{tx.from_name || '—'}
                    {tx.sub_account_name ? ` · ${tx.sub_account_name}` : ''}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(tx.created_at, lang)}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
        <Panel testId="owner-division-bar" className="p-5">
          <SectionTitle>{t.by_division}</SectionTitle>
          {stats.by_division.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-14 text-center">{t.no_data}</p>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={stats.by_division} margin={{ top: 14, right: 8, bottom: 0, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                       tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={38} />
                <RTooltip formatter={(v) => [fmtKr(v), t.collected]} cursor={{ fill: '#f0fdfa' }}
                          contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {stats.by_division.map((_, i) => <Cell key={i} fill={TEAL[i % TEAL.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel testId="owner-payment-mix" className="p-5">
          <SectionTitle>{t.payment_mix}</SectionTitle>
          {mixTotal === 0 ? (
            <p className="text-[13px] text-slate-400 py-14 text-center">{t.no_data}</p>
          ) : (
            <div className="flex items-center">
              <ResponsiveContainer width="55%" height={200}>
                <PieChart>
                  <Pie data={mixData} dataKey="value" innerRadius={52} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                    {mixData.map((_, i) => <Cell key={i} fill={TEAL[i]} />)}
                  </Pie>
                  <RTooltip formatter={(v, n, p) => [fmtKr(v), p?.payload?.label]}
                            contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {mixData.map((m, i) => (
                  <div key={m.key} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: TEAL[i] }} />
                    {m.key === 'app2app' ? <Smartphone size={14} className="text-slate-400" /> : <QrCode size={14} className="text-slate-400" />}
                    <span className="text-[12.5px] text-slate-600 flex-1">{m.label}</span>
                    <span className="text-[13px] font-semibold tabular-nums">{mixTotal ? Math.round((m.value / mixTotal) * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Usage panels */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel testId="owner-seats-panel" className="p-5">
          <div className="flex items-center justify-between mb-4">
            <SectionTitle>{t.seats_plan}</SectionTitle>
            <Badge className="bg-teal-600 text-white shadow-none hover:bg-teal-600 gap-1"><Crown size={11} />{usage.plan_name || '—'}</Badge>
          </div>
          <div className="space-y-4">
            {[['seats', t.seats_used], ['divisions', t.divisions_used], ['swish_accounts', t.swish_used]].map(([key, label]) => (
              <div key={key}>
                <div className="flex justify-between text-[12.5px] mb-1.5">
                  <span className="text-slate-500">{usage[key]?.used ?? 0} / {usage[key]?.limit ?? '∞'} {label}</span>
                </div>
                <Meter used={usage[key]?.used ?? 0} limit={usage[key]?.limit} />
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full h-8 text-[13px] border-slate-200" onClick={() => setNav('billing')}>
              <CreditCard size={14} className="mr-1.5" />{t.manage_plan}
            </Button>
          </div>
        </Panel>
        <Panel className="p-5 flex flex-col justify-between" testId="owner-pending-panel">
          <SectionTitle>{t.pending_inv}</SectionTitle>
          <div className="text-[34px] font-semibold text-teal-700 tabular-nums" style={WORK_SANS}>{usage.pending_invitations ?? 0}</div>
          <Button variant="outline" size="sm" className="w-full h-8 text-[13px] border-slate-200" onClick={() => setNav('members')}>{t.view_all}</Button>
        </Panel>
        <Panel className="p-5 flex flex-col justify-between" testId="owner-swish-panel">
          <SectionTitle>{t.swish_used}</SectionTitle>
          <div className="text-[34px] font-semibold text-teal-700 tabular-nums" style={WORK_SANS}>{usage.swish_accounts?.used ?? 0}</div>
          <Button variant="outline" size="sm" className="w-full h-8 text-[13px] border-slate-200" onClick={() => setNav('swish')}>{t.view_all}</Button>
        </Panel>
      </div>
    </div>
  );
}
